// Shared destination-address handling for both flag-trip modals. A flag-trip dropoff must
// resolve to at least a street, not a bare city/region — dispatch can't route a driver to
// "Cleveland, OH, USA". A house/building number is NOT required (a street-hail dropoff is
// often "the corner of Main & 5th", no exact number) — a street name is enough. `types:
// ['geocode']` keeps the Autocomplete open to street-level results (not just full addresses
// with a number); the post-selection check below is what actually rejects city-only picks.

// No `types` filter at all, deliberately. The history here is two narrowings that each
// fixed one case and broke another:
//   'address'  — only results carrying a house/building number, so a street-hail dropoff
//                typed as a plain street ("cleve" -> Cleveland Ave) offered nothing.
//   'geocode'  — fixed streets, but still EXCLUDES establishments, so real dropoffs like
//                "cleveland hopkins international airport", a hospital or a hotel offered
//                no suggestions either. The driver then typed the name in full, nothing
//                ever resolved, and the raw typed text was submitted as the destination
//                with no coordinates — the bug this replaces.
// Unfiltered Autocomplete returns addresses, streets AND establishments, which is the
// full set of places a driver actually gets hailed to. isSpecificAddress() below is what
// rejects a too-vague pick; the type filter was never the right tool for that.
export const ADDRESS_AUTOCOMPLETE_OPTIONS = {
  fields: ['geometry', 'formatted_address', 'address_components', 'name', 'types'],
};

function addressComponent(place, type) {
  return place.address_components?.find((c) => c.types.includes(type))?.short_name || '';
}

/** Same shape as extractDestination, but for a Geocoder result (reverse geocode),
 *  whose geometry.location is a google.maps.LatLng just like a Places result. */
function extractFromGeocoderResult(result, lat, lng) {
  if (!result) return null;
  return {
    address: result.formatted_address || '',
    lat,
    lon: lng,
    streetNumber: addressComponent(result, 'street_number'),
    streetName: addressComponent(result, 'route'),
    city:
      addressComponent(result, 'locality') ||
      addressComponent(result, 'sublocality') ||
      addressComponent(result, 'administrative_area_level_2'),
    state: addressComponent(result, 'administrative_area_level_1'),
    postalCode: addressComponent(result, 'postal_code'),
  };
}

/** Reverse-geocode GPS coords into the same address shape used for destinations.
 *  Resolves to null (never rejects) when Maps isn't ready or nothing is found, so
 *  callers can treat a missing pickup address as non-fatal. */
export function reverseGeocodeCoords(lat, lng) {
  return new Promise((resolve) => {
    if (!window.google?.maps?.Geocoder || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      resolve(null);
      return;
    }
    try {
      new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
        if (status !== 'OK' || !results?.length) {
          resolve(null);
          return;
        }
        // Prefer a result that actually carries a street; fall back to the first.
        const withStreet = results.find((r) =>
          r.address_components?.some((c) => c.types.includes('route'))
        );
        resolve(extractFromGeocoderResult(withStreet || results[0], lat, lng));
      });
    } catch {
      resolve(null);
    }
  });
}

/** Extracts {address, lat, lon, streetNumber, streetName, city, state, postalCode} from a
 *  Google Places result, or null if it has no geometry (nothing selected yet). */
export function extractDestination(place) {
  if (!place?.geometry) return null;
  // Google's own formatted_address is always preferred: it is the canonical, fully
  // qualified string ("5300 Riverside Dr, Cleveland, OH 44135, USA") rather than the
  // fragment the driver typed. place.name is only a fallback for the rare result that
  // carries no formatted_address.
  return {
    address: place.formatted_address || place.name || '',
    // Kept so an establishment (airport/hospital/hotel) can be recognised as a specific
    // destination even though it has no street "route" component, and so the UI can show
    // the venue name alongside its address.
    placeName: place.name || '',
    placeTypes: Array.isArray(place.types) ? place.types : [],
    lat: place.geometry.location.lat(),
    lon: place.geometry.location.lng(),
    streetNumber: addressComponent(place, 'street_number'),
    streetName: addressComponent(place, 'route'),
    city: addressComponent(place, 'locality') || addressComponent(place, 'sublocality') || addressComponent(place, 'administrative_area_level_2'),
    state: addressComponent(place, 'administrative_area_level_1'),
    postalCode: addressComponent(place, 'postal_code'),
  };
}

export const VAGUE_ADDRESS_MESSAGE = 'Choose a specific street — not just a city.';

// Common street-suffix words, used as a fallback when Google's address_components don't
// carry a "route" type for a selection (happens for some intersections/segments) even
// though the visible text is clearly a street. Word-boundary match, case-insensitive.
const STREET_SUFFIX_RE =
  /\b(street|st|avenue|ave|road|rd|boulevard|blvd|drive|dr|lane|ln|way|place|pl|court|ct|highway|hwy|parkway|pkwy|circle|cir|terrace|ter|trail|trl|square|sq)\b\.?/i;

// Street name is enough; an exact house/building number is not required. Prefer the
// structured "route" component; fall back to scanning the formatted text for a street
// suffix so a genuine street-level pick isn't rejected just because Google didn't tag
// it with a "route" component this time.
// Establishment place types that are a real, routable destination in their own right.
// An airport or hospital has exact coordinates and a canonical formatted_address, but no
// "route" component — judging it by street suffix alone wrongly called it vague and, in
// strict mode, refused a perfectly good dropoff.
const ROUTABLE_PLACE_TYPES = [
  'establishment',
  'point_of_interest',
  'premise',
  'subpremise',
  'airport',
  'transit_station',
  'train_station',
  'bus_station',
  'hospital',
  'lodging',
  'shopping_mall',
  'university',
  'school',
  'stadium',
];

export function isSpecificAddress(destination) {
  if (!destination) return false;
  if (destination.streetName || destination.streetNumber) return true;
  // A named establishment resolves to one building, which is precisely what dispatch
  // needs to route to — more precise than a bare street, not less.
  if ((destination.placeTypes || []).some((t) => ROUTABLE_PLACE_TYPES.includes(t))) return true;
  return STREET_SUFFIX_RE.test(destination.address || '');
}
